import React, { useEffect, useState } from 'react';
import { FileText, Folder, Loader2, Pencil, Save, UserPlus, X } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { RichTextEditor } from './RichTextEditor';
import { resourcesAPI } from '../lib/api';

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

export const ResourceDocDialog = ({ doc, canEdit, startEditing, onClose, onSaved, onOpenSeed, onAssign }) => {
  const [editing, setEditing] = useState(false);
  const [html, setHtml] = useState('');
  const [titulo, setTitulo] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (doc) { setHtml(doc.html || ''); setTitulo(doc.titulo || ''); setEditing(!!startEditing && canEdit); } }, [doc, startEditing, canEdit]);

  const save = async () => {
    setSaving(true);
    try { const { item } = await resourcesAPI.update(doc.id, { html, titulo }); toast.success('Documento guardado'); setEditing(false); onSaved(item); }
    catch (e) { toast.error(e.message || 'No se pudo guardar'); } finally { setSaving(false); }
  };
  const upload = async (f) => { try { return (await resourcesAPI.uploadDocImage(f)).url; } catch (e) { toast.error(e.message); return null; } };
  const onClickCapture = (e) => {
    const a = e.target.closest('a');
    const href = a?.getAttribute('href') || '';
    if (href.startsWith('#doc:')) { e.preventDefault(); e.stopPropagation(); onOpenSeed(href.slice(5)); }
  };

  return (
    <Dialog open={!!doc} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="resource-doc-dialog">
        <DialogHeader>
          <div className="flex items-start justify-between gap-3 pr-6">
            <div className="min-w-0 flex-1">
              {editing ? <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} className="text-lg font-semibold" data-testid="resource-doc-title-input" />
                : <DialogTitle className="text-xl flex items-center gap-2"><FileText className="w-5 h-5 text-primary shrink-0" />{doc?.titulo}</DialogTitle>}
              <DialogDescription className="mt-1 flex items-center gap-2 flex-wrap text-xs">
                {doc?.categoria && <span className="inline-flex items-center gap-1"><Folder className="w-3 h-3" />{doc.categoria}</span>}
                <span>Actualizado el {fmt(doc?.updated_at)}{doc?.updated_by ? ` por ${doc.updated_by}` : ''}</span>
              </DialogDescription>
            </div>
            {canEdit && (editing ? (
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setHtml(doc.html || ''); setTitulo(doc.titulo || ''); }} data-testid="resource-doc-cancel-btn"><X className="w-4 h-4 mr-1" />Cancelar</Button>
                <Button size="sm" onClick={save} disabled={saving} data-testid="resource-doc-save-btn">{saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Guardar</Button>
              </div>
            ) : (
              <div className="flex gap-2 shrink-0">
                {onAssign && <Button size="sm" variant="outline" onClick={() => onAssign(doc, doc.html || '')} data-testid="resource-doc-assign-btn"><UserPlus className="w-4 h-4 mr-1" />Guardar copia en…</Button>}
                <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="resource-doc-edit-btn"><Pencil className="w-4 h-4 mr-1" />Editar</Button>
              </div>
            ))}
          </div>
        </DialogHeader>
        <div onClickCapture={onClickCapture} data-testid="resource-doc-body">
          {doc && (editing
            ? <RichTextEditor value={html} onChange={setHtml} minHeight={400} sticky={false} onImageUpload={upload} testId="resource-doc-editor" />
            : <RichTextEditor key={doc.id + (doc.updated_at || '')} value={doc.html || ''} editable={false} testId="resource-doc-view" />)}
        </div>
      </DialogContent>
    </Dialog>
  );
};
